import { ApiError } from '@composables/useApi';

const STORAGE_KEY = 'otp-autosend';

// Extracts the structured `retry_after` (seconds) the backend now returns in the
// body of a `429 otp_rate_limited` response, so the OTP screens can show an
// exact resend countdown instead of a vague "try again later". Returns null when
// the field is absent or not a positive number.
export const getRetryAfterFromError = (error: unknown): number | null => {
	if (error instanceof ApiError) {
		const retryAfter = (error.response as { retry_after?: unknown })?.retry_after;
		if (typeof retryAfter === 'number' && retryAfter > 0) {
			return retryAfter;
		}
	}
	return null;
};

interface OtpAutoSendState {
	email: string;
	sentAt: number;
	retryAfter: number;
}

// Tracks the OTP auto-send triggered by the ?email= query param so that
// reloading the page restores the code-entry step instead of sending
// another email while the resend window is still active.

export const getAutoSentRemaining = (email: string): number | null => {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;

		const state: OtpAutoSendState = JSON.parse(raw);
		if (state.email !== email || typeof state.sentAt !== 'number' || typeof state.retryAfter !== 'number') {
			return null;
		}

		const elapsed = Math.floor((Date.now() - state.sentAt) / 1000);
		return Math.max(state.retryAfter - elapsed, 0);
	} catch (e) {
		return null;
	}
};

export const markAutoSent = (email: string, retryAfter: number) => {
	const state: OtpAutoSendState = {
		email,
		sentAt: Date.now(),
		retryAfter,
	};
	sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export const clearAutoSent = () => {
	sessionStorage.removeItem(STORAGE_KEY);
};
